import Vue from 'vue'
import App from './App.vue'
import vuetify from './plugins/vuetify';
import router from './router'
import axios from 'axios';
import Loading from "./components/cLoading";
import moment from 'moment'
import "leaflet/dist/leaflet.css";
import store from './store';
import PerfectScrollbar from 'vue2-perfect-scrollbar';
import 'vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css';
import Zendesk from './common/zendesk.js';
import VueGtm from '@gtm-support/vue2-gtm';
import vueDebounce from 'vue-debounce';
import EventGatewayPlugin from './plugins/events.js'
import VueColumnsResizable from 'vue-columns-resizable';
import cookieConsent from './common/cookieConsent';
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, BarElement, CategoryScale, LinearScale } from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, ArcElement, CategoryScale, LinearScale);

const getInitials = function (input) {
	if (!input) return "";

	const names = input.split(" ");
	let initials = names[0].substring(0, 1).toUpperCase();

	if (names.length > 1) {
		initials += names[names.length - 1].substring(0, 1).toUpperCase();
	}
	return initials;
}

Vue.prototype.$loginState = {
	isAuthenticated: false, user: null, reEnterPassword: false, sso: false, canQA: false, readOnly: false, canEditDocParts: false, canDemo: false,
	rmapiAccessToken: null,
	permittedRoutes: [],
	defaultRoute: "",
	ui_layout: "V1",
	setUser: function (u) {
		u.impersonatedInitials = getInitials(u.name);
		u.userInitials = getInitials(u.impersonator_name || u.name);
		this.user = u;
		this.impersonating = u && !!u.impersonator_name;
		this.clientUser = u.clientUser;
		this.noAccess = u.noAccess;
		this.canQA = u.canQA;
		this.canDemo = u.canDemo;
		this.canEditUsers = u.canEditUsers;
		this.onDemand = u.onDemand;
		this.canAdmin = u.canAdmin;
		this.canImpersonate = u.canImpersonate;
		this.readOnly = u.readOnly;
		this.canEditDocParts = u.canEditDocParts;
		this.isInternal = u.client_type === 'INTERNAL' || u.impersonator_client_type === 'INTERNAL';
		this.ui_layout = u.ui_layout;
		localStorage.setItem("RMTAGKEY", JSON.stringify({ email: u.email, name: u.name, impersonator_name: u.impersonator_name, client: u.client, groups: u.permission_groups }));
	},
	saveSetting: function (key, value) {
		let user_id = this.user ? this.user.user_id : '';
		localStorage.setItem(`${key}|${user_id}`, JSON.stringify(value));
	},
	removeSetting: function (key) {
		let user_id = this.user ? this.user.user_id : '';
		localStorage.removeItem(`${key}|${user_id}`);
	},
	getSetting: function (key, defaultValue) {
		let user_id = this.user ? this.user.user_id : 0;
		let raw = localStorage.getItem(`${key}|${user_id}`);
		if (raw === "[object Object]")
			return defaultValue || {};
		else
			return JSON.parse(raw) || defaultValue || {};
	},
	getUserSetting: function (key) {
		if (!this.user || !this.user.userSettings) return null;
		const setting = this.user.userSettings.find(s => s.us_type == key);
		if (!setting) return null;
		return setting.value;
	},
	saveUserSetting(key, value) {
		if (!this.user) return;
		if (!this.user.userSettings) this.user.userSettings = [];

		axios
			.post("user/updateSetting/", { type: key, value: value })
			.then((resp) => {
				if (resp.data.Status === "OK") {
					const setting = this.user.userSettings.find(s => s.us_type == key);
					if (setting) setting.value = value;
					else this.user.userSettings.push({ us_type: key, value: value });
				}
			})
			.catch((err) => {
				if (err.response && err.response.status === 401) {
					this.sessionExpired(err);
				} else {
					console.log(err);
					this.response = err.response
						? err.response.data
						: { message: "Unexpected Error" };
				}
			});
	},
	getLandingConfig(route) {
		return this.user?.landingPageConfigs?.find(c => c.page_route === route) || {};
	},
	updateLandingDefinition(id, definition) {
		if (this.user && this.user.landingPageConfigs) {
			const config = this.user.landingPageConfigs.find(lp => lp.lpd_id === id);
			if (config)
				config.definition = definition;
		}
	},
	saveUserViewSetting(view_name, setting) {
		if (!this.user) return;
		if (!this.user.userViewSettings) this.user.userViewSettings = [];
		axios
			.post("user/updateUserViewSetting/", { view_name: view_name, setting: setting })
			.then((resp) => {
				if (resp.data.Status === "OK") {
					const view = this.user.userViewSettings.find(s => s.view_name === view_name);
					if (view) view.setting = setting;
					else this.user.userViewSettings.push({ view_name: view_name, setting: setting });
				}
			})
			.catch((err) => {
				if (err.response && err.response.status === 401) {
					this.sessionExpired(err);
				} else {
					console.log(err);
					this.response = err.response
						? err.response.data
						: { message: "Unexpected Error" };
				}
			});
	},
	async getUserViewSetting (view_name) {
		if (!this.user) return null;
		if (!this.user.userViewSettings) this.user.userViewSettings = [];
		let view = this.user.userViewSettings.find(s => s.view_name == view_name);
		if (!view) {
			await axios
			.get("user/userViewSetting/" + view_name)
			.then((resp) => {
				if (resp.data.Status === "OK") {
					view = { view_name: view_name, setting: resp.data.Data };
					this.user.userViewSettings.push(view);
				}
			})
			.catch((err) => {
				if (err.response && err.response.status === 401) {
					this.sessionExpired(err);
				} else {
					console.log(err);
					this.response = err.response
						? err.response.data
						: { message: "Unexpected Error" };
				}
			});
		}
		return view ? view.setting : null;
	},
	checkForPermission(setting) {
		if (this.user && this.user.settings.some(x => x.setting === setting && x.value)) {
			const required = this.user.settings.find(x => x.setting === setting).value.split("|");
			return required.some(r => this.user.permission_groups.some(pg => pg === r))
		} else {
			return false;
		}
	},
};

Vue.prototype.$passedParams = {
	params: {
		view: "",
		docTypes: [],
		filterString: "",
		statuses: [],
		section: "",
		title: "",
		sourcePath: "",
		sourceView: "",
		sourceTitle: "",
	},
	get() {
		const val = { ...this.params };
		this.params.view = "";
		this.params.docTypes = [];
		this.params.filterString = "";
		this.params.statuses = [];
		this.params.section = "";
		this.params.title = "";
		this.params.sourcePath = "";
		this.params.sourceView = "";
		this.params.sourceTitle = "";
		return val;
	},
	set(val) {
		this.params.view = val.view || "";
		this.params.docTypes = val.docTypes && Array.isArray(val.docTypes) ? val.docTypes : [];
		this.params.filterString = val.filterString || "";
		this.params.statuses = val.statuses && Array.isArray(val.statuses) ? val.statuses : [];
		this.params.section = val.section || "";
		this.params.title = val.title || "";
		this.params.sourcePath = val.sourcePath || "";
		this.params.sourceView = val.sourceView || "";
		this.params.sourceTitle = val.sourceTitle || "";
	}
}

Vue.config.productionTip = false
Vue.filter('dateDoMMMYY', function (value) {
	if (value) {
		return moment(new Date(value)).format('Do MMM YYYY')
	}
});
Vue.filter('dateDoMMMYYHHmm', function (value) {
	if (value) {
		return moment(new Date(value)).format('Do MMM YYYY HH:mm')
	}
});
Vue.use(PerfectScrollbar);
// eslint-disable-next-line vue/multi-word-component-names
Vue.component('Loading', Loading);
Vue.use(vueDebounce);
Vue.use(VueColumnsResizable);
Vue.use(EventGatewayPlugin, {});
Vue.directive('testid', {
  bind(el, binding) {
    // if (process.env.NODE_ENV !== 'production') {
      el.setAttribute('data-testid', binding.value);
    // }
  }
});

const zenDeskKey = process.env.VUE_APP_ZENDESK_KEY;
Vue.use(Zendesk, {
	key: zenDeskKey,
	disabled: true,
	hideOnLoad: false,
	settings: {
		webWidget: {
			offset: {
				horizontal: '5px',
				vertical: '45px'
			},
			zIndex: 300
		},
	}
})

window.showSupport = function () {
	vue.$zendesk.show();
}
window.toggleSupport = function () {
	vue.$zendesk.toggle();
}
window.hideSupport = function () {
	vue.$zendesk.hide();
}

// GTM is only loaded once the user has given consent for analytics cookies.
let gtmRegistered = false;
function registerGtm() {
	if (gtmRegistered) return;
	if (!process.env.VUE_APP_GTM_ID) return;
	Vue.use(VueGtm, {
		id: process.env.VUE_APP_GTM_ID,
		defer: false,
		enabled: true,
		debug: true,
		loadScript: true,
		vueRouter: router,
		ignoredViews: ["homepage"],
		trackOnNextTick: false,
	});
	Vue.prototype.$config = {
		GTM_AUTH: process.env.VUE_APP_GTM_AUTH,
		GTM_COOKIES_WIN: process.env.VUE_APP_GTM_COOKIES_WIN,
		GTM_ID: process.env.VUE_APP_GTM_ID,
		GTM_PREVIEW: process.env.VUE_APP_GTM_PREVIEW
	};
	gtmRegistered = true;
}

if (cookieConsent.hasValidConsent() && cookieConsent.getConsent().analytics) {
	registerGtm();
}

cookieConsent.onConsentChange((next, prev) => {
	if (next.analytics && (!prev || !prev.analytics)) {
		registerGtm();
	}
	// Opt-out (analytics true -> false) is handled by cCookiePreferences
	// reloading the page after save, since vue2-gtm cannot be cleanly
	// unloaded at runtime.
});

// bootstrap
const vue = new Vue({
	vuetify: vuetify,
	router,
	store,
	//components: { Loading },
	provide: {
		loginState: Vue.prototype.$loginState,
		store, router,
		passedParams: Vue.prototype.$passedParams
	},	
	render: h => h(App)
}).$mount('#app');

window.addEventListener('beforeunload', () => {
	vue.$destroy();
});
